const KARACHI_OFFSET_HOURS = 5;

/**
 * Get current date in Karachi timezone as YYYY-MM-DD string
 * @returns {string} Date string in YYYY-MM-DD format
 */
export const getCurrentDateInKarachi = () => {
  const now = new Date();
  // Get UTC time and add Karachi offset
  const karachiTime = new Date(
    now.getTime() + KARACHI_OFFSET_HOURS * 60 * 60 * 1000
  );
  return karachiTime.toISOString().split("T")[0];
};

/**
 * Convert a date string (YYYY-MM-DD) to a proper Date object
 * Ensures the date is interpreted as Karachi timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Date object
 */
export const parseKarachiDate = (dateString) => {
  if (!dateString) return null;
  // Parse as local time to avoid timezone conversion issues
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format a date string for display in en-GB locale
 * @param {string} dateStringOrDate - Date string in YYYY-MM-DD format or ISO timestamp
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatKarachiDate = (dateStringOrDate, options = {}) => {
  if (!dateStringOrDate) return "-";
  
  // Handle Date objects or ISO timestamps (strings with 'T')
  if (dateStringOrDate instanceof Date || (typeof dateStringOrDate === "string" && dateStringOrDate.includes("T"))) {
    const dateToObject = dateStringOrDate instanceof Date ? dateStringOrDate : new Date(dateStringOrDate);
    if (isNaN(dateToObject.getTime())) return "-";
    
    // If time options are requested (hour, minute, second), format with time
    if (options.hour || options.minute || options.second) {
      return dateToObject.toLocaleString("en-GB", {
        timeZone: "Asia/Karachi",
        ...options,
      });
    }
    
    // Otherwise, convert to date-only and format as before
    const dateStr = dateToObject.toISOString().split("T")[0];
    return formatKarachiDate(dateStr, options);
  } else if (typeof dateStringOrDate === "string") {
    const date = parseKarachiDate(dateStringOrDate);
    if (!date) return "";

    return date.toLocaleDateString("en-GB", {
      timeZone: "Asia/Karachi",
      ...options,
    });
  } else {
    return "Invalid Type";
  }
};

/**
 * Get day of week for a date string (0 = Sunday, 6 = Saturday)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {number} Day of week
 */
export const getDayOfWeek = (dateString) => {
  const date = parseKarachiDate(dateString);
  return date ? date.getDay() : null;
};

/**
 * Add days to a date string
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {number} days - Number of days to add
 * @returns {string} New date string in YYYY-MM-DD format
 */
export const addDays = (dateString, days) => {
  const date = parseKarachiDate(dateString);
  if (!date) return dateString;

  date.setDate(date.getDate() + days);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Compare two date strings
 * @param {string} date1 - First date string in YYYY-MM-DD format
 * @param {string} date2 - Second date string in YYYY-MM-DD format
 * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export const compareDates = (date1, date2) => {
  if (date1 === date2) return 0;
  return date1 < date2 ? -1 : 1;
};

/**
 * Check if a date string is today in Karachi timezone
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if the date is today
 */
export const isToday = (dateString) => {
  return dateString === getCurrentDateInKarachi();
};

/**
 * Check if a date string is in the past (before today)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if the date is in the past
 */
export const isPast = (dateString) => {
  return compareDates(dateString, getCurrentDateInKarachi()) < 0;
};

/**
 * Check if a date string is in the future (after today)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if the date is in the future
 */
export const isFuture = (dateString) => {
  return compareDates(dateString, getCurrentDateInKarachi()) > 0;
};
